const clone = value => JSON.parse(JSON.stringify(value));

export const toDisplayText = (value, fallback = '') => {
  if (value == null) return fallback;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') return toDisplayText(value.name ?? value.label ?? value.value, fallback);
  return fallback;
};

export const edgeId = edge => [
  edge.source_node_key || edge.source,
  edge.source_port || edge.sourceHandle,
  edge.target_node_key || edge.target,
  edge.target_port || edge.targetHandle,
  Number(edge.input_order ?? 0),
].join('::');

export function backendNodeToFlow(node) {
  return {
    id: node.node_key,
    type: node.type,
    position: node.position_json && Number.isFinite(Number(node.position_json.x)) && Number.isFinite(Number(node.position_json.y))
      ? { x: Number(node.position_json.x), y: Number(node.position_json.y) }
      : { x: 0, y: 0 },
    data: {
      nodeType: node.type,
      name: toDisplayText(node.name) || null,
      config: clone(node.config_json || {}),
    },
  };
}

export function backendEdgeToFlow(edge) {
  return {
    id: edgeId(edge),
    source: edge.source_node_key,
    sourceHandle: edge.source_port,
    target: edge.target_node_key,
    targetHandle: edge.target_port,
    data: { inputOrder: Number(edge.input_order ?? 0) },
  };
}

export const backendGraphToFlow = graph => ({
  nodes: (graph.nodes || []).map(backendNodeToFlow),
  edges: (graph.edges || []).map(backendEdgeToFlow),
});

export function flowNodeToBackend(node) {
  return {
    node_key: node.id,
    type: node.data.nodeType || node.type,
    name: toDisplayText(node.data.name) || null,
    config_json: clone(node.data.config || {}),
    position_json: { x: Number(node.position.x), y: Number(node.position.y) },
  };
}

export function flowEdgeToBackend(edge) {
  return {
    source_node_key: edge.source,
    source_port: edge.sourceHandle,
    target_node_key: edge.target,
    target_port: edge.targetHandle,
    input_order: Number(edge.data?.inputOrder ?? 0),
  };
}

export const flowGraphToBackend = ({ nodes, edges, defaultTimezone, defaultFrequency }) => ({
  nodes: nodes.map(flowNodeToBackend),
  edges: edges.map(flowEdgeToBackend),
  default_timezone: defaultTimezone,
  default_frequency: defaultFrequency || null,
});

export function generateNodeKey(type, nodes) {
  const base = String(type || 'node').replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^[^A-Za-z]+/, '') || 'node';
  const used = new Set(nodes.map(node => node.id));
  let suffix = 1;
  let candidate = `${base}_${suffix}`;
  while (used.has(candidate)) candidate = `${base}_${++suffix}`;
  return candidate.slice(0, 64);
}

export function addGraphNode(nodes, type, position = { x: 80, y: 80 }, initialConfig = {}) {
  const id = generateNodeKey(type, nodes);
  return [...nodes, {
    id,
    type,
    position,
    data: { nodeType: type, name: null, config: clone(initialConfig) },
  }];
}

const definitionFor = (catalog, type) => catalog?.node_types?.find(item => item.type === type);

export function connectGraphEdge(nodes, edges, connection, catalog) {
  const source = nodes.find(node => node.id === connection.source);
  const target = nodes.find(node => node.id === connection.target);
  const sourceDefinition = definitionFor(catalog, source?.data?.nodeType || source?.type);
  const targetDefinition = definitionFor(catalog, target?.data?.nodeType || target?.type);
  const output = sourceDefinition?.outputs?.[connection.sourceHandle];
  const input = targetDefinition?.inputs?.[connection.targetHandle];
  if (!source || !target || !output || !input || output.dataType !== input.dataType) {
    return { edges, error: 'La conexión no es compatible con los puertos seleccionados.' };
  }
  const existing = edges.filter(edge => edge.target === connection.target && edge.targetHandle === connection.targetHandle);
  const maximum = input.multiple ? input.maxConnections : 1;
  if (maximum != null && existing.length >= maximum) {
    return { edges, error: 'La entrada seleccionada no admite más conexiones.' };
  }
  const nextOrder = existing.reduce((maximumOrder, edge) => Math.max(maximumOrder, Number(edge.data?.inputOrder ?? 0)), -1) + 1;
  const edge = {
    ...connection,
    id: edgeId({ ...connection, input_order: nextOrder }),
    data: { inputOrder: nextOrder },
  };
  if (edges.some(item => item.id === edge.id)) return { edges, error: 'La conexión ya existe.' };
  return { edges: [...edges, edge], error: null };
}

export function removeGraphNodes(nodes, edges, nodeIds) {
  const removed = new Set(nodeIds);
  return {
    nodes: nodes.filter(node => !removed.has(node.id)),
    edges: edges.filter(edge => !removed.has(edge.source) && !removed.has(edge.target)),
  };
}
